import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import { servicesApi, homepageApi } from '../lib/api';
import { ServiceCard } from '../components/ServiceCard';
import { Link } from 'react-router-dom';

interface HomepageSection {
  id: number;
  key: string;
  title: string | null;
  content: string | null;
  imageUrl: string | null;
  buttonText: string | null;
  buttonLink: string | null;
  order: number;
  isActive: boolean;
}

export function HomePage() {
  const { data: services, isLoading } = useQuery({
    queryKey: ['services', true],
    queryFn: () => servicesApi.getAll(true).then((res) => res.data),
  });

  const { data: sections, isLoading: sectionsLoading } = useQuery({
    queryKey: ['homepage-sections'],
    queryFn: () => homepageApi.getSections().then((res) => res.data),
  });

  const getSectionByKey = (key: string): HomepageSection | undefined => {
    return sections?.find((s: HomepageSection) => s.key === key);
  };

  const renderSection = (section: HomepageSection) => {
    const isHero = section.key === 'hero';
    const isAIAutomation = section.key === 'ai-automation';
    const isAIContent1 = section.key === 'ai-content-1';
    const isAIContent2 = section.key === 'ai-content-2';
    const isImageText = section.key === 'image-text' || section.key.startsWith('image-text-');
    const isTextImage = section.key === 'text-image' || section.key.startsWith('text-image-');
    const isServices = section.key === 'services';

    if (isHero) {
      return (
        <section key={section.id} className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
          <div className="container-custom">
            <div className="max-w-3xl">
              {section.title && (
                <h1 className="text-4xl md:text-5xl font-bold mb-6" dangerouslySetInnerHTML={{ __html: section.title }} />
              )}
              {section.content && (
                <p className="text-xl mb-8 text-primary-100" dangerouslySetInnerHTML={{ __html: section.content }} />
              )}
              {(section.buttonText || section.buttonLink) && (
                <div className="flex flex-col sm:flex-row gap-4">
                  {section.buttonText && section.buttonLink && (
                    <Link
                      to={section.buttonLink}
                      className="btn bg-white text-primary-600 hover:bg-gray-100"
                    >
                      {section.buttonText}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (isAIAutomation) {
      return (
        <section key={section.id} className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
          <div className="container-custom">
            <div className="max-w-3xl">
              {section.title && (
                <h2 className="text-4xl md:text-5xl font-bold mb-6" dangerouslySetInnerHTML={{ __html: section.title }} />
              )}
              {section.content && (
                <p className="text-xl mb-8 text-primary-100" dangerouslySetInnerHTML={{ __html: section.content }} />
              )}
              {(section.buttonText || section.buttonLink) && (
                <div className="flex flex-col sm:flex-row gap-4">
                  {section.buttonText && section.buttonLink && (
                    <Link
                      to={section.buttonLink}
                      className="btn bg-white text-primary-600 hover:bg-gray-100"
                    >
                      {section.buttonText}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (isAIContent1) {
      return (
        <section key={section.id} className="py-16 bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
              {section.imageUrl && (
                <div className="order-2 md:order-1">
                  <img
                    src={section.imageUrl}
                    alt={section.title || 'Yapay Zeka Otomasyonları'}
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              {section.content && (
                <div className="order-1 md:order-2">
                  {section.title && (
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 text-gray-800" dangerouslySetInnerHTML={{ __html: section.title }} />
                  )}
                  <p className="text-xl md:text-2xl text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content }} />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (isAIContent2 || isTextImage) {
      return (
        <section key={section.id} className="py-16 bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {section.content && (
                <div className="order-1 md:order-1">
                  {section.title && (
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 text-gray-800" dangerouslySetInnerHTML={{ __html: section.title }} />
                  )}
                  <p className="text-xl md:text-2xl text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content }} />
                </div>
              )}
              {section.imageUrl && (
                <div className="order-2 md:order-2">
                  <img
                    src={section.imageUrl}
                    alt={section.title || 'İçerik'}
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (isImageText) {
      return (
        <section key={section.id} className="py-16 bg-white">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {section.imageUrl && (
                <div className="order-2 md:order-1">
                  <img
                    src={section.imageUrl}
                    alt={section.title || 'İçerik'}
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              {section.content && (
                <div className="order-1 md:order-2">
                  {section.title && (
                    <h3 className="text-2xl md:text-3xl font-bold mb-4 text-gray-800" dangerouslySetInnerHTML={{ __html: section.title }} />
                  )}
                  <p className="text-xl md:text-2xl text-gray-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content }} />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (isServices) {
      return (
        <section key={section.id} className="py-8 bg-gray-50">
          <div className="container-custom">
            {section.title && (
              <h2 className="text-3xl font-bold text-center mb-8" dangerouslySetInnerHTML={{ __html: section.title }} />
            )}
            {isLoading ? (
              <div className="text-center py-12">Yükleniyor...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services?.slice(0, 6).map((service: any) => (
                  <ServiceCard
                    key={service.id}
                    slug={service.slug}
                    title={service.title}
                    summary={service.summary}
                    imageUrl={service.images?.[0]?.url}
                  />
                ))}
              </div>
            )}
            <div className="text-center mt-8">
              <Link to="/cozumler" className="btn btn-primary">
                Tüm Çözümleri Görüntüle
              </Link>
            </div>
          </div>
        </section>
      );
    }

    // Generic section
    return (
      <section key={section.id} className="py-16 bg-white">
        <div className="container-custom">
          {section.title && (
            <h2 className="text-3xl font-bold mb-6" dangerouslySetInnerHTML={{ __html: section.title }} />
          )}
          {section.imageUrl && (
            <img
              src={section.imageUrl}
              alt={section.title || ''}
              className="w-full h-auto rounded-lg shadow-lg mb-6"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {section.content && (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: section.content }} />
          )}
          {section.buttonText && section.buttonLink && (
            <div className="mt-6">
              <Link
                to={section.buttonLink}
                className="btn btn-primary"
              >
                {section.buttonText}
              </Link>
            </div>
          )}
        </div>
      </section>
    );
  };

  // Fallback to default content if no sections found
  const heroSection = getSectionByKey('hero');
  const servicesSection = getSectionByKey('services');
  const aiAutomationSection = getSectionByKey('ai-automation');
  const aiContent1Section = getSectionByKey('ai-content-1');
  const aiContent2Section = getSectionByKey('ai-content-2');

  return (
    <>
      <Helmet>
        <title>Ana Sayfa - {import.meta.env.VITE_APP_NAME || 'KUMBİLGİ'}</title>
        <meta
          name="description"
          content={`${import.meta.env.VITE_APP_NAME || 'KUMBİLGİ'} teknoloji çözümleri - Donanım desteği, web tasarım, mobil uygulama ve daha fazlası`}
        />
      </Helmet>

      {sectionsLoading ? (
        <div className="text-center py-12">Yükleniyor...</div>
      ) : sections && sections.length > 0 ? (
        // Render dynamic sections
        sections.map((section: HomepageSection) => renderSection(section))
      ) : (
        // Fallback to default content
        <>
          {/* Hero Section */}
          <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
            <div className="container-custom">
              <div className="max-w-3xl">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Teknoloji Çözümlerinde Yılların Deneyimi
                </h1>
                <p className="text-xl mb-8 text-primary-100">
                  Donanım desteğinden web tasarıma, mobil uygulamadan otomasyona kadar
                  geniş bir hizmet yelpazesi ile yanınızdayız.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/cozumler" className="btn bg-white text-primary-600 hover:bg-gray-100">
                    Çözümlerimizi Keşfedin
                  </Link>
                  <Link to="/iletisim" className="btn bg-primary-700 text-white hover:bg-primary-600">
                    Hemen İletişime Geçin
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-8 bg-gray-50">
            <div className="container-custom">
              <h2 className="text-3xl font-bold text-center mb-8">Çözümlerimiz</h2>
              {isLoading ? (
                <div className="text-center py-12">Yükleniyor...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services?.slice(0, 6).map((service: any) => (
                    <ServiceCard
                      key={service.id}
                      slug={service.slug}
                      title={service.title}
                      summary={service.summary}
                      imageUrl={service.images?.[0]?.url}
                    />
                  ))}
                </div>
              )}
              <div className="text-center mt-8">
                <Link to="/cozumler" className="btn btn-primary">
                  Tüm Çözümleri Görüntüle
                </Link>
              </div>
            </div>
          </section>

          {/* AI Automation Section */}
          <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
            <div className="container-custom">
              <div className="max-w-3xl">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Yapay Zeka Destekli Otomasyonlar
                </h2>
                <p className="text-xl mb-8 text-primary-100">
                  Şirketinizin ihtiyaçlarına uygun butik olarak geliştirilen ve sizi rakiplerinizden bir adım öne çıkaran otomasyonlar için bizimle iletişime geçin.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://www.nigta.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn bg-white text-primary-600 hover:bg-gray-100"
                  >
                    www.nigta.com
                  </a>
                  <Link to="/yapay-zeka-otomasyonlar" className="btn bg-primary-700 text-white hover:bg-primary-600">
                    Bilgi Al
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* AI Automation Content Section */}
          <section className="py-16 bg-white">
            <div className="container-custom">
              {/* First Image with Text */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                <div className="order-2 md:order-1">
                  <img
                    src="https://img.yurdusev.com/yz1.jpg"
                    alt="Yapay Zeka Otomasyonları"
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="order-1 md:order-2">
                  <p className="text-xl md:text-2xl text-gray-800 leading-relaxed">
                    Biz sizin için YZ otomasyonu oluşturalım siz sadece arkanıza yaslanıp teknolojinin sizi taşıdığı yerin konforunu yaşayın
                  </p>
                </div>
              </div>

              {/* Second Text with Image - Symmetric */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="order-1 md:order-1">
                  <p className="text-xl md:text-2xl text-gray-800 leading-relaxed">
                    Karmaşık işlere son verip bunu teknolojinin yapmasına izin verin hatayı en aza düşürüp performansı ve kazanımları en üst seviyeye taşıdığını göreceksiniz
                  </p>
                </div>
                <div className="order-2 md:order-2">
                  <img
                    src="https://img.yurdusev.com/yz2.jpg"
                    alt="Yapay Zeka Otomasyonları"
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}

